import { useEffect, useMemo, useRef } from 'react';
import { AssetType, IFeedSpread, OrderActivity, OrderLeg, OrderStrategyType, SpreadType } from '@tradingblock/types';
import { useQuoteActions, useQuoteSymbolSubscriptionFromGlobalState } from '../../../hooks/useQuoteData';
import { useStateSelector, accountIdSelector, subAccountIdSelector } from '../../../data/global/dataSelectors';
import { getOrdersByBlockId, getPositionAndOrderSymbols } from '../../../data/global/selectors/orderSelector';
import { useBlockId } from '../../../components/blocks/BlockState';
import { useActiveBlockTab } from '../../../components/blocks/BlockAccessors';
import { useActivityTimeframeValues } from '../data/useActivityTimeframe';
import { useActivityBlockData, useActivitySettingsValue } from '../useActivitySettings';
import { isOptionPastCalendarExpirationDate } from '../../../utilities/optionSymbolExpiration';
import { OrderStrategyIdToOrderStrategyName } from '../../Order/state/OrderStateToOrder';
import { useDispatch } from 'react-redux';
import { Actions } from '../../../data/global/actions';
import _ from 'lodash';

function getSpreadType(strategy: number): keyof typeof SpreadType {
  const spreadTypeMap: { [key: number]: keyof typeof SpreadType } = {
    // mappedStrategies output values
    1: 'Vertical',
    2: 'Calendar',
    // OrderStrategyTypeId enum values
    3: 'Calendar',
    5: 'Straddle',
    6: 'Strangle',
    7: 'Butterfly',
    8: 'Condor',
    9: 'IronButterfly',
    10: 'IronCondor',
    13: 'CoveredCall',
    14: 'MarriedPut',
    15: 'Conversion',
    16: 'Collar',
    26: 'CoveredPut',
    27: 'MarriedCall',
    28: 'Calendar',
    29: 'Calendar',
  };
  if ([0, 18, 11, 17, 20, 21, 25].includes(strategy)) {
    return 'Custom';
  }
  return spreadTypeMap[strategy] || 'Custom';
}

function mappedStrategies(StrategyType: OrderStrategyType): number {
  if (StrategyType && typeof StrategyType === 'string') {
    if (StrategyType.includes('Vertical')) return 1;
    if (StrategyType.includes('Calendar')) return 2;
    if (StrategyType.includes('Butterfly')) return 3;
    if (StrategyType.includes('Condor')) return 4;
    if (StrategyType.includes('IronButterfly')) return 5;
    if (StrategyType.includes('IronCondor')) return 6;
    if (StrategyType.includes('Collar')) return 7;
  }
  const strategyStr = StrategyType && StrategyType.toString ? StrategyType.toString() : '1';
  return parseInt(strategyStr, 10);
}

function getSpreadInfo(spreadType: SpreadType, legs: OrderLeg[]) {
  let legArray: IFeedSpread['Legs'] = [];
  let name = '';
  legs.forEach(val => {
    legArray = [...legArray, {
      Symbol: '.' + val.Symbol,
      AssetType: val.AssetType,
      Quantity: val.Action === 2 ? -1 : 1,
      Multiplier: val.SpreadRatio ? val.SpreadRatio : 1,
    }];
    name += val.Symbol + `_${val.Action === 2 ? -1 : 1}_${val.SpreadRatio ? val.SpreadRatio : 1}-`;
  });
  name = name.slice(0, name.length - 1);
  name = name.replace(/\./g, '');
  return { name, legArray };
}

function buildSpreadDefinition(order: OrderActivity): IFeedSpread & { id?: number } {
  const strategyNum = mappedStrategies(order.StrategyType as OrderStrategyType);
  const spreadTypeKey = getSpreadType(strategyNum);
  const spreadType = SpreadType[spreadTypeKey];
  const { name, legArray } = getSpreadInfo(spreadType, order.Legs);
  return {
    id: order.OrderId, // Include OrderId for websocket subscription
    name: name,
    type: spreadType,
    Legs: legArray,
  };
}

/**
 * Filters out spreads that contain option legs past calendar expiration.
 */
function filterExpiredOptionSpreads(spreads: (IFeedSpread & { id?: number })[]): (IFeedSpread & { id?: number })[] {
  return spreads.filter(spread => {
    const hasExpiredLeg = spread.Legs.some(leg => {
      const symbol = leg.Symbol.startsWith('.') ? leg.Symbol.substring(1) : leg.Symbol;
      if (leg.AssetType === AssetType.Option) {
        return isOptionPastCalendarExpirationDate(symbol);
      }
      return false;
    });
    return !hasExpiredLeg;
  });
}

/**
 * Hook to subscribe to quotes and spreads for Activity block orders
 * Subscribes to quotes for all order legs and underlying symbols
 * Subscribes to spreads for multi-leg orders
 */
export function useActivityBlockSubscriptions() {
  const blockId = useBlockId();
  const orderHistory = useStateSelector(getOrdersByBlockId(blockId));
  const { setQuotes, setSpreads } = useQuoteActions();
  const activeTab = useActiveBlockTab();

  const allOrdersIds = useMemo(() => {
    if (!orderHistory || !Array.isArray(orderHistory)) {
      return [];
    }
    return _.uniq(orderHistory.map(order => {if (order.OrderId) return order.OrderId; return 0;}));
  }, [orderHistory]);
  
  const prevOrderHistory = useRef<OrderActivity[]>([]);
  
  const allPrevOrdersIds = useMemo(() => {
    if (!prevOrderHistory.current || !Array.isArray(prevOrderHistory.current)) {
      return [];
    }
    return _.uniq(prevOrderHistory.current.map(order => {if (order.OrderId) return order.OrderId; return 0;}));
  }, [prevOrderHistory.current]);
  
  
  useEffect(() => {
    const changedData = !_.isEqual(allOrdersIds, allPrevOrdersIds);
    if (changedData) {
      prevOrderHistory.current = orderHistory || [];
    } 
  }, [allOrdersIds, allPrevOrdersIds, orderHistory]);

  const quotesAndSpreadsActivity = useMemo(() => {
    if (!orderHistory || orderHistory.length === 0) {
      return [];
    }

    // Sort by OrderId descending (matching table sorting)
    const sorted = [...orderHistory].sort((a: OrderActivity, b: OrderActivity) => {
      const aId = a.OrderId || 0;
      const bId = b.OrderId || 0;
      return bId - aId; // Descending order
    });
    
    // Deduplicate by OrderId (keep first occurrence)
    const deduplicated = _.uniqBy(sorted, 'OrderId');
    
    return deduplicated;
  }, [orderHistory]);
  
  // Extract quote symbols and build spread definitions
  const { quoteSymbols, spreadDefinitions } = useMemo(() => {
    const symbols = new Set<string>();
    const spreads: (IFeedSpread & { id?: number })[] = [];
    
    quotesAndSpreadsActivity.forEach((order: OrderActivity) => {
      if (!order.Legs || order.Legs.length === 0 || !order.StrategyType) {
        return;
      }
      
      // Get strategy name from OrderStrategyType
      const strategyName = OrderStrategyIdToOrderStrategyName(
        parseInt(order.StrategyType.toString())
      );
      
      // Check if it's a shares/stock order strategy
      const isSharesStrategy = strategyName === 'Shares' || strategyName === 'Stock';
      
      // Check if it's an option strategy (Call, Put, Vertical, Calendar, Butterfly, etc.)
      const isOptionStrategy = strategyName === 'Call' || 
                               strategyName === 'Put' ||
                               strategyName === 'Vertical' ||
                               strategyName === 'Call Vertical' ||
                               strategyName === 'Put Vertical' ||
                               strategyName === 'Calendar' ||
                               strategyName === 'Call Calendar' ||
                               strategyName === 'Put Calendar' ||
                               strategyName === 'Straddle' ||
                               strategyName === 'Strangle' ||
                               strategyName === 'Butterfly' ||
                               strategyName === 'Call Butterfly' ||
                               strategyName === 'Put Butterfly' ||
                               strategyName === 'Condor' ||
                               strategyName === 'Call Condor' ||
                               strategyName === 'Put Condor' ||
                               strategyName === 'Iron Butterfly' ||
                               strategyName === 'Iron Condor' ||
                               strategyName === 'Box Spread' ||
                               strategyName === 'Covered Call' ||
                               strategyName === 'Married Put' ||
                               strategyName === 'Conversion' ||
                               strategyName === 'Collar' ||
                               strategyName === 'Custom';
      
      // Handle Single strategy - check if it's shares or option based on leg AssetType
      const isSingleStrategy = strategyName === 'Single';
      let isSingleShares = false;
      if (isSingleStrategy && order.Legs.length === 1) {
        isSingleShares = order.Legs[0].AssetType === AssetType.Equity;
      }
      
      // If it's a shares order strategy, subscribe to quotes only
      if (isSharesStrategy) {
        order.Legs.forEach(leg => {
          if (leg.Symbol) {
            symbols.add(leg.Symbol);
          }
        });
        // // Also add underlying symbol if it exists
        // if (order.UnderlyingSymbol) {
        //   symbols.add(order.UnderlyingSymbol);
        // }
      }
      // If it's an option order strategy, subscribe to quotes for single-leg and spreads for multi-leg
      else if (isOptionStrategy || (isSingleStrategy && !isSingleShares)) {
        if (order.Legs.length === 1) {
          // Single-leg options subscribe to quotes, not spreads
          if (order.Legs[0].Symbol) {
            symbols.add(order.Legs[0].Symbol);
          }
        } else if (order.OrderId && order.StrategyType && order.Legs.length > 1) {
          const spreadDef = buildSpreadDefinition(order);
          spreads.push(spreadDef);
        }
      }
    });
    
    return {
      quoteSymbols: Array.from(symbols),
      spreadDefinitions: spreads,
    };
  }, [quotesAndSpreadsActivity]);
    
  const validSpreadDefinitions = useMemo(() => {
    return filterExpiredOptionSpreads(spreadDefinitions);
  }, [spreadDefinitions]);

  // Subscribe to quotes (only valid/non-expired symbols)
  useEffect(() => {
    // Call setQuotes to update Redux state
    // DataFeedInitializer will handle deduplication and incremental updates
    setQuotes(quoteSymbols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteSymbols]);
  
  
  // Subscribe to spreads (only valid/non-expired spreads)
  // With pagination, we subscribe only to spreads for orders on the current page
  useEffect(() => {
    // Call setSpreads to update Redux state
    // DataFeedInitializer will handle deduplication and incremental updates
    setSpreads(validSpreadDefinitions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validSpreadDefinitions]); // Include currentTabTitle to ensure rerun on tab change
  
  // Separate effect for cleanup on unmount only
  useEffect(() => {
    return () => {
      setQuotes([]);
      setSpreads([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run cleanup on unmount
  
}

